// @LTG-ID: F-061
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: 07_PAGES_AND_ROUTES.md, 08_FEATURES.md#F-061,
//        ORCH-0050 reinforcement #2 (LTG-styled 404 for /journals/:slug catch-all)
// @CHANGED: 2026-02 session_004 (CHG-0058)
// @OWNER: web
//
// F-061 — LTG-styled 404 component mounted at the `/journals/:slug` catch-all
// route. Renders only when the requested slug does not match a registered
// published-brief route. Visual register stays in the editorial palette
// (Source Serif 4 + gold accent) because the route IS a /journals/* surface;
// only the "browser-default 404" is what reinforcement #2 was rejecting.
//
// "Did you mean..." list reflects the routes that resolve TODAY:
//   - P3-M2 (current): Brief 01 only
//   - P3-M3 (next):    Brief 02 will join — update the PUBLISHED_BRIEFS array
//                       in this file in CHG-0061 when M3 registers Brief 02.

import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import "./journal-not-found.css";

// Single source of truth for "what published briefs do we suggest in the 404?"
// Update this array as new explicit brief routes register.
const PUBLISHED_BRIEFS = [
    {
        slug: "what-is-my-business-worth",
        number: "01",
        title:
            "What is my business actually worth? An owner's primer on business valuation",
    },
    {
        slug: "sde-vs-ebitda",
        number: "02",
        title:
            "SDE vs EBITDA: which multiple actually applies to your business",
    },
    // P3-M3 (CHG-0062) added Brief 02; future briefs append here as they
    // register explicit routes in App.js.
];

export const JournalNotFound = () => {
    const { slug } = useParams();

    useEffect(() => {
        document.title = "Brief not found · The KGOB Journals";
    }, []);

    return (
        <section
            className="ltg-journal-not-found"
            data-testid="journal-not-found"
            aria-labelledby="journal-not-found-h1"
        >
            <div className="ltg-journal-not-found__inner">
                <span
                    className="ltg-journal-not-found__eyebrow"
                    data-testid="journal-not-found-eyebrow"
                >
                    404 &middot; Brief not yet published
                </span>
                <h1
                    id="journal-not-found-h1"
                    className="ltg-journal-not-found__h1"
                    data-testid="journal-not-found-h1"
                >
                    We have not published a brief at that slug yet.
                </h1>
                <p
                    className="ltg-journal-not-found__lede"
                    data-testid="journal-not-found-lede"
                >
                    The KGOB Journals catalog 37 editorial briefs across seven
                    sections. Two are written in full today; the remaining
                    thirty-five are summarized on the index page with their
                    publish dates. The slug you followed is not currently
                    routed.
                </p>
                <p
                    className="ltg-journal-not-found__slug-row"
                    data-testid="journal-not-found-slug"
                >
                    Requested slug: <code>{slug || "(none)"}</code>
                </p>

                <div
                    className="ltg-journal-not-found__suggest"
                    data-testid="journal-not-found-suggest"
                >
                    <span className="ltg-journal-not-found__suggest-label">
                        Did you mean
                    </span>
                    <ul>
                        {PUBLISHED_BRIEFS.map((b) => (
                            <li key={b.slug}>
                                <Link
                                    to={`/journals/${b.slug}`}
                                    data-testid={`journal-not-found-suggest-${b.slug}`}
                                >
                                    Brief {b.number} &middot; {b.title}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div
                    className="ltg-journal-not-found__cta-row"
                    data-testid="journal-not-found-cta-row"
                >
                    <Link
                        to="/journals"
                        className="ltg-journal-not-found__cta-primary"
                        data-testid="journal-not-found-back-to-index"
                    >
                        &larr; Back to the Journals index
                    </Link>
                    <Link
                        to="/"
                        className="ltg-journal-not-found__cta-secondary"
                        data-testid="journal-not-found-back-to-home"
                    >
                        Home
                    </Link>
                </div>
            </div>
        </section>
    );
};

export default JournalNotFound;
